/**
 * Shared formatting utilities for dates, severity labels, and display values.
 */

import type { Severity } from '@/types/finding';
import type { ScanStatus } from '@/types/scan';

/** Format an ISO timestamp for display (e.g., "Jan 15, 2026 3:42 PM"). */
export function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

/** Format a short date without time (e.g., "Jan 15, 2026"). */
export function formatShortDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/** Map severity to a human-readable label with proper casing. */
export function severityLabel(severity: Severity): string {
  return severity.charAt(0).toUpperCase() + severity.slice(1);
}

/** Map severity to a Tailwind text color class. */
export function severityColor(severity: Severity): string {
  const map: Record<Severity, string> = {
    critical: 'text-severity-critical',
    high: 'text-severity-high',
    medium: 'text-severity-medium',
    low: 'text-severity-low',
    info: 'text-severity-info',
  };
  return map[severity];
}

/** Map severity to a Tailwind background color class (for badges). */
export function severityBgColor(severity: Severity): string {
  const map: Record<Severity, string> = {
    critical: 'bg-red-100 text-red-800',
    high: 'bg-orange-100 text-orange-800',
    medium: 'bg-yellow-100 text-yellow-800',
    low: 'bg-blue-100 text-blue-800',
    info: 'bg-gray-100 text-gray-600',
  };
  return map[severity];
}

/** Map scan status to a Tailwind color class. */
export function statusColor(status: ScanStatus): string {
  const map: Record<ScanStatus, string> = {
    pending: 'text-status-pending',
    running: 'text-status-running',
    completed: 'text-status-completed',
    failed: 'text-status-failed',
    cancelled: 'text-status-cancelled',
  };
  return map[status];
}

/** Map scan status to badge styling. */
export function statusBgColor(status: ScanStatus): string {
  const map: Record<ScanStatus, string> = {
    pending: 'bg-gray-100 text-gray-700',
    running: 'bg-blue-100 text-blue-700',
    completed: 'bg-green-100 text-green-700',
    failed: 'bg-red-100 text-red-700',
    cancelled: 'bg-gray-100 text-gray-500',
  };
  return map[status];
}

/** A completed scan with failed engines keeps its results but is labelled "Completed with errors". */
export function completedWithErrors(scan: { status: ScanStatus; error_message: string | null }): boolean {
  return scan.status === 'completed' && !!scan.error_message;
}

/** Format a CVSS score for display (e.g., "9.8" or "—"). */
export function formatCvss(score: number | null | undefined): string {
  if (score === null || score === undefined) return '—';
  return score.toFixed(1);
}

/** Format file size in human-readable form. */
export function formatFileSize(bytes: number | null | undefined): string {
  if (!bytes) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
